import Axios from 'axios';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
const API_URL = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_PUBLISHABLE_KEY);

// Function to check if clinic has added payment details
export const checkPaymentDetails = async (setHasPaymentDetails) => {
    try {
        const response = await Axios.get(`${API_URL}/payment/check-payment-details`, {
            headers: {
                Authorization: localStorage.getItem("token"),
            }
        });
        if (response.status === 200) {
            setHasPaymentDetails(response.data.hasPaymentDetails);
        } else {
            console.error('Error fetching payment details');
        }
    } catch (error) {
        console.error('Error to check payment details: ', error);
    }
};


// Function to setup payment method/card
export const handlePaymentSetup = async (setLoading) => {
    try {
        setLoading(true);  // Indicate loading state
        const response = await Axios.post(`${API_URL}/payment/create-checkout-session`, {}, {
            headers: {
                Authorization: localStorage.getItem("token"),
            },
        });

        if (response.status === 200) {
            const stripe = await stripePromise;
            await stripe.redirectToCheckout({ sessionId: response.data.sessionId });
        } else {
            console.error('Error creating Stripe checkout session');
        }
    } catch (error) {
        console.error('Error in API request:', error);
    } finally {
        setLoading(false);
    }
};


// Function to validate payment session ID
export const validateSessionId = async (sessionId, setSuccess, setError, setLoading) => {
    try {
        setLoading(true);
        const response = await Axios.get(`${API_URL}/payment/payment-success?session_id=${sessionId}`);

        if (response.status === 200 && response.data.success) {
            setSuccess(true);
        } else {
            setError('Failed to verify payment session');
        }
    } catch (error) {
        setError('Error verifying payment session');
    } finally {
        setLoading(false);
    }
};

// Function to book appointment with payment gateway
export const handleBookingConfirmation = async (
  prebookingFormData,
  selectedDate,
  selectedTime,
  typeOfCare,
  navigate,
  setIsModalOpen,
  setLoading,
  selectedClinicId
) => {
    if (String(selectedDate).trim() === '' || String(selectedTime).trim() === '' || String(typeOfCare).trim() === '') {
        toast.error('Please enter all the required fields', {
            position: 'top-center',
        });
        return;
    }
    try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            toast.error('Please log in to book an appointment', { position: 'top-center' });
            return;
        }

        // Initiate Stripe Checkout session
        const response = await Axios.post(
            `${API_URL}/payment/initiate-booking-payment`,
            {
                ...prebookingFormData,
                bookingDate: selectedDate,
                bookingTime: selectedTime,
                typeOfCare: typeOfCare,
                selectedClinicId
            },
            {
                headers: {
                    Authorization: token
                }
            }
        );

        if (response.data?.assignedProvider) {
            const { name } = response.data.assignedProvider;
            toast.info(`You already have an ongoing appointment with provider ${name}`, {
                position: 'top-center',
            });
            return;
        }

        if (response.status === 200) {
            setIsModalOpen(false);
            const stripe = await stripePromise;
            const { sessionId } = response.data;
            const { error } = await stripe.redirectToCheckout({ sessionId });
            if (error) {
                throw new Error(error.message);
            }
            // Note: Booking confirmation happens after Stripe redirect (handled in success_url)
        }
    } catch (error) {
        console.error('Error initiating booking payment:', error);
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            toast.error('Session expired. Please log in again.', { position: 'top-center' });
        } else {
            toast.error('Failed to initiate payment. Please try again.', { position: 'top-center' });
        }
    } finally {
        setLoading(false);
    }
};

// Function to initiate stripe connect
export const initiateStripeConnect = async (setLoading) => {
    try {
        setLoading(true);
        const response = await Axios.post(
            `${API_URL}/payment/stripe-connect`,
            {},
            {
                headers: {
                    Authorization: localStorage.getItem('token')
                }
            }
        );
        if (response.status === 200) {
            window.location.href = response.data.redirectUrl; // Redirect to Stripe onboarding
        } else {
            toast.error('Failed to initiate Stripe Connect', { position: 'top-center' });
        }
    } catch (error) {
        console.error('Error initiating Stripe Connect:', error);
        toast.error('Server error', { position: 'top-center' });
    } finally {
        setLoading(false);
    }
};